import { useSelector } from 'react-redux';
import { IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useActionsConfig } from 'reactApp/hooks/useActionsConfig';
import { getFacesFileId } from 'reactApp/modules/faces/faces.helpers';
import { hasFacesOnFile } from 'reactApp/modules/faces/faces.selectors';
import { getFeatureMobileGalleryGrid3 } from 'reactApp/modules/features/features.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getSort } from 'reactApp/modules/sort/sort.selectors';
import { EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import {
    getActionsBlock,
    getActionsItems,
    getFacesOnFileItem,
    getFileInfoItem,
    getFilterBlock,
    getRemoveBlock,
    getSortBlock,
    getViewBlock,
} from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.data';
import { IOptionBlock, Props } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.types';

import { useCallbacks } from './useCallbacks';
import { useFilterBlock } from './useFilterBlock';

type UseItemsHook = (
    props: Props & { storage: EStorageType; isFolder: boolean; weblink: boolean; isGalleryCategory: boolean }
) => IOptionBlock[];

export const useItems: UseItemsHook = ({
    id,
    storage,
    onlyActions = false,
    onClose,
    skipRename,
    sendAnalytics,
    weblink,
    isFolder,
    isGalleryCategory,
}) => {
    const viewMode = useSelector((state) => SettingsSelectors.getViewByStorage(state, storage));
    const sort = useSelector((state: RootState) => getSort(state, { id }, storage));
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isNewMobileGalleryGridEnabled = useSelector(getFeatureMobileGalleryGrid3);

    const { changeSortOrder, onUpload, onDownload, resetFilter } = useCallbacks({ onClose, storage, id, sendAnalytics });
    const actionsConfig = useActionsConfig({
        id: id === ROOT_FOLDER_ID ? '' : id,
        skipUpload: onlyActions || IS_PUBLIC_ALBUM,
        skipRename,
        isMobile: true,
    });
    const actionsItems = getActionsItems({ isAnonymous, id, onUpload, onDownload, weblink, sendAnalytics });

    const { filtered, filterItems } = useFilterBlock({ storage, onlyActions, facesFilter: Boolean(actionsConfig[EActions.facesFilter]) });
    const fileId = getFacesFileId(id);
    const fileHasFaces = useSelector((state: RootState) => hasFacesOnFile(state, fileId));
    const isViewerActive = useSelector(ViewerSelectors.isViewerActive);

    const actionsList = Object.keys(actionsConfig)
        .map((id) => actionsItems[id])
        .filter((action) => action);

    // Добавляем пункт 'Люди на фото' в OptionsModal просмотрщика.
    if (fileHasFaces && isViewerActive) {
        actionsList.push(getFacesOnFileItem({ fileId }));
    }

    actionsList.push(getFileInfoItem({ id, isFolder }));

    const hasRemoveBlock = Boolean(actionsConfig[EActions.remove]);

    const filterBlock = getFilterBlock({ filtered, items: filterItems, resetFilter });

    const blocksList: IOptionBlock[] = [];

    if (filtered && filterItems.length) {
        blocksList.push(filterBlock);
    }

    // Не показывать "Действия с папкой" на галерее.
    if (!isGalleryCategory) {
        blocksList.push(getActionsBlock({ actionsList, onlyActions }));
    }

    if (hasRemoveBlock) {
        blocksList.push(getRemoveBlock({ id, sendAnalytics }));
    }

    if (onlyActions) {
        return blocksList;
    }

    blocksList.push(getViewBlock({ viewMode, storage, isNewMobileGalleryGridEnabled }));

    if (!filtered && filterItems.length) {
        blocksList.push(filterBlock);
    }

    if (!isGalleryCategory) {
        blocksList.push(getSortBlock({ changeSortOrder, sort }));
    }

    return blocksList;
};
